/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  DatePicker,
  Tabs,
  message,
} from 'antd';
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import Direccion from 'components/Direccion';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  deleteZonaExclusiva,
  getZonaExclusiva,
  getZonaExclusivas,
  patchZonaExclusiva,
  postZonaExclusiva,
} from 'api/empresas/zonasExclusivas';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';
import { getTiposDeZona } from 'api/catalogos';
import NumericInput from 'components/NumericInput';
import { getEmpresa, getEmpresas } from 'api/empresas/empresas';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import address from 'utils/address';
import { useSelector } from 'react-redux';
import { isMutable } from 'utils/estadosGlobales';

const { TabPane } = Tabs;
const baseURI = 'empresas/zonas-exclusivas/';

const models = ['tipodezonaexclusiva'];

const ZonaExclusiva = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const { estadosGlobales } = useSelector(({ catalogs }) => catalogs);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [tipoDeZona, setTipoDeZona] = useState([]);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [empresaFound, setEmpresaFound] = useState([]);
  const [mutable, setMutable] = useState(true);

  const basicKeys = ['general', 'info'];
  const complexForms = [
    { key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getZonaExclusivas({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _tiposDeZona] = await Promise.all([
        getZonaExclusivas({ page: 1 }, true),
        getTiposDeZona(),
      ]);
      if (mounted) {
        setData(_data);
        setTipoDeZona(_tiposDeZona);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const fetchEmpresa = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _empresa = await getEmpresa(id?.id || id);
        if (_empresa) {
          setTimeout(() => {
            form.setFieldsValue({
              empresa: _empresa.id,
            });
          });
          setEmpresaFound({
            ..._empresa,
            nombre_comercial: `${_empresa?.clave} - ${_empresa?.nombre_comercial}`,
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };
  const resetAndValidate = () => {
    setEmpresaFound([]);
    formInfo.resetFields(['q']);
    formInfo.validateFields(['q']);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setEmpresaFound([]);
    setCurrentTabKey('general');
    setMutable(true);
  };

  const onClickEdit = async (ZonaExclusivaId) => {
    const key = selectedRowKeys[0] || ZonaExclusivaId;
    const match = await getZonaExclusiva(key);
    if (match) {
      setMutable(isMutable(match.estados_globales));
      await fetchEmpresa(match.empresa?.id);
      form.setFieldsValue(match);
    }
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const _data = await (key ? patchZonaExclusiva(key, values) : postZonaExclusiva(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteZonaExclusiva(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _empresas = await getEmpresas({ q });
        if (_empresas.results.length) {
          const [selected] = _empresas.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound([{
            ...selected,
            nombre_comercial: `${selected?.clave || ''} - ${selected?.nombre_comercial}`,
          }]);
        } else if (!_empresas.results.length) {
          message.info('No se encontró la empresa');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Empresa',
      dataIndex: 'empresa',
      key: 'empresa',
      render: (val) => (val?.nombre_comercial),
      width: 200,
    },
    {
      titleText: 'Fecha de contratacion',
      dataIndex: 'fecha_de_contratacion',
      key: 'fecha_de_contratacion',
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
      width: 200,
    },
    {
      titleText: 'Tipo de Zona',
      dataIndex: 'tipo_de_zona_exclusiva',
      key: 'tipo_de_zona_exclusiva',
      width: 150,
      catalog: {
        cat: tipoDeZona,
        labelProp: 'descripcion',
        use: true,
      },
    },
    {
      titleText: 'Metros Contratados',
      dataIndex: 'metros_contratados',
      key: 'metros_contratados',
      width: 200,
    },
    {
      titleText: 'Dirección',
      dataIndex: 'direccion',
      key: 'direccion',
      width: 300,
      render: (val) => address(val),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: true,
      },
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              rowKey="id"
              baseURI={baseURI}
              permission={permission}
              pagination={false}
              paginationCallback={fetchData}
              count={data.count}
              currentPage={currentPage}
              hasAllViewPermission={hasAllViewPermission}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ZonaExclusiva`}
                extra={(
                  <FormSubmitControls
                    onFinish={getOnFinish()}
                    onCancel={onCancel}
                    mutable={mutable}
                    onCancelText="Cancelar"
                    permission={permission}
                    allowSaveAndContinue
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={mutable ? onChangeTabKey : setCurrentTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      initialValues={{ estados_globales: 1 }}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <InputSearchPro
                          tooltip="Búsqueda por Razon social"
                          label="Empresa"
                          onClickAdvanced={() => setVisibleBusquedaPadrones(true)}
                          name="q"
                          disabled={!mutable}
                          rules={rules.required}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 16,
                          }}
                          inputProps={{
                            onPressEnter: () => searchEmpresa(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidate();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 16,
                            },
                            name: 'empresa',
                            label: 'Empresa',
                            dataSource: empresaFound,
                            labelProp: 'nombre_comercial',
                            onClear: resetAndValidate,
                          }}
                        />

                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_de_contratacion"
                            label="fecha de contratacion"
                            rules={rules.required}
                            hasFeedback
                            required
                          >
                            <DatePicker
                              disabled={!mutable}
                              format={DATE_FRONTEND_FORMAT}
                              placeholder=""
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            name="metros_contratados"
                            label="Metros Contratados"
                            required
                            hasFeedback
                            disabled={!mutable}
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="requiere_pintura"
                            rules={rules.required}
                            label="¿Requiere pintura?"
                            hasFeedback
                          >
                            <Select trueFalse disabled={!mutable} />
                          </Form.Item>
                        </Col>
                        <Col lg={8} md={16} sm={24} xs={24}>
                          <Form.Item
                            name="requiere_senalamiento"
                            rules={rules.required}
                            label="¿Requiere señalamientos?"
                            hasFeedback
                          >
                            <Select trueFalse disabled={!mutable} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="tipo_de_zona_exclusiva"
                            rules={rules.required}
                            label="Tipo de zona exclusiva"
                            hasFeedback
                          >
                            <Select disabled={!mutable} dataSource={tipoDeZona} />
                          </Form.Item>
                        </Col>
                        { !!selectedRowKeys.length && (
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              name="clave"
                              label="clave"
                              rules={rules.required}
                              hasFeedback
                              required
                            >
                              <Input disabled allowClear />
                            </Form.Item>
                          </Col>
                        )}
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="estados_globales"
                            label="Estados Globales"
                            hasFeedback
                            allowClear
                          >
                            <Select disabled={!mutable} dataSource={estadosGlobales} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={24}>
                          <Form.Item
                            name="observaciones"
                            label="Observacion"
                            hasFeedback
                          >
                            <Input.TextArea
                              autoSize={{ minRows: 4, maxRows: 5 }}
                              allowClear
                              disabled={!mutable}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                  {addressPermission?.view && (
                    <TabPane
                      tab={(
                        <>
                          <EnvironmentOutlined />
                          Dirección
                        </>
                      )}
                      key="direccion"
                      forceRender
                      disabled={!selectedRowKeys.length && !mutable}
                    >
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="misma_direccion_que_empresa"
                          label="misma direccion de empresa?"
                        >
                          <Select trueFalse disabled={!mutable} />
                        </Form.Item>
                      </Col>
                      <Direccion
                        currentURI={baseURI}
                        parentID={selectedRowKeys[0]}
                        setLoading={setLoading}
                        setFormAddress={setFormAddress}
                        setCurrentTabKey={setCurrentTabKey}
                        addressPropName="direccion"
                        callback={(values, _continue) => {
                          if (!_continue) {
                            onCancel();
                          } else {
                            const [key] = selectedRowKeys;
                            const clone = [...data];
                            const mappedData = clone.map((e) => (e.id === key
                              ? ({ ...e, direccion: values?.id }) : e));
                            setData(mappedData);
                          }
                        }}
                        permission={addressPermission}
                      />
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="ZonaExclusiva seleccionada"
          loading={loading}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaPadrones}
          setVisible={setVisibleBusquedaPadrones}
          callback={fetchEmpresa}
          tipoDePadron={2}
          allowAdd={false}
        />
      </Spin>
    </Row>
  );
};

export default ZonaExclusiva;
